import { ERROR_MESSAGES, envelopeSchema, type ErrorCode, type PageMeta } from '@rp/contracts'
import { z } from 'zod'
import { API_COPY } from './copy'
import { ApiError, isApiError } from './error'

export { ApiError, isApiError }

export const SERVICE_SIGNAL_KINDS = [
  'suggestions_paused',
  'classification_degraded',
  'channel_disconnected',
  'publishing_failing',
  'database_outage',
] as const
export type ServiceSignalKind = (typeof SERVICE_SIGNAL_KINDS)[number]
export type ServiceSignal = { kind: ServiceSignalKind; detail: string; at: string }

export type QueryValue = string | number | boolean | undefined
export type ApiRequest = {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  query?: Record<string, QueryValue>
  body?: unknown
  idempotencyKey?: string
  signal?: AbortSignal
}
export type ApiResult<T> = { data: T; meta?: PageMeta }
export type ApiDownloadRequest = Pick<ApiRequest, 'path' | 'query' | 'signal'>
export type ApiDownload = { blob: Blob; filename: string | null }

export type ApiClientOptions = {
  baseUrl: string
  getTenantId: () => string | null
  onSignal?: (signal: ServiceSignal) => void
  onUnauthenticated?: () => void
  fetchImpl?: typeof fetch
  now?: () => Date
}

export interface ApiClient {
  request<T>(schema: z.ZodType<T>, req: ApiRequest): Promise<ApiResult<T>>
  download(req: ApiDownloadRequest): Promise<ApiDownload>
}

const searchParams = (query: Record<string, QueryValue> | undefined): string => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) params.set(key, String(value))
  }
  const serialised = params.toString()
  return serialised === '' ? '' : `?${serialised}`
}

const signalFor = (path: string, code: ErrorCode, status: number): ServiceSignalKind | null => {
  if ((path.startsWith('/readyz') || path.startsWith('/v1/me')) && (status === 0 || status >= 500)) return 'database_outage'
  if (path.includes('/suggest') && (code === 'UPSTREAM_UNAVAILABLE' || code === 'BUDGET_EXHAUSTED')) return 'suggestions_paused'
  if (path.includes('/reply') && code === 'UPSTREAM_UNAVAILABLE') return 'publishing_failing'
  return null
}

const isFormData = (body: unknown): body is FormData => body instanceof FormData

const bodyOf = (body: unknown): BodyInit => (isFormData(body) ? body : JSON.stringify(body))

const buildHeaders = (tenantId: string | null, req: ApiRequest): Headers => {
  const headers = new Headers({ accept: 'application/json' })
  if (tenantId !== null) headers.set('x-tenant-id', tenantId)
  if (req.body !== undefined && !isFormData(req.body)) headers.set('content-type', 'application/json')
  if (req.idempotencyKey !== undefined) headers.set('idempotency-key', req.idempotencyKey)
  return headers
}

const buildInit = (tenantId: string | null, req: ApiRequest): RequestInit => ({
  method: req.method,
  credentials: 'same-origin',
  headers: buildHeaders(tenantId, req),
  ...(req.body === undefined ? {} : { body: bodyOf(req.body) }),
  ...(req.signal === undefined ? {} : { signal: req.signal }),
})

const UNICODE_FILENAME = /filename\*=UTF-8''([^;]+)/i
const PLAIN_FILENAME = /filename="([^"]+)"/i
const ATTACHMENT = /^\s*attachment\b/i

const decodedFilename = (encoded: string): string | null => {
  try {
    return decodeURIComponent(encoded)
  } catch {
    return null
  }
}

export const filenameOfDisposition = (disposition: string | null): string | null => {
  if (disposition === null) return null
  const unicode = UNICODE_FILENAME.exec(disposition)?.[1]
  const decoded = unicode === undefined ? null : decodedFilename(unicode)
  return decoded ?? PLAIN_FILENAME.exec(disposition)?.[1] ?? null
}

export const defaultFetch = (input: RequestInfo | URL, init?: RequestInit) => fetch(input, init)

export const createApiClient = (options: ApiClientOptions): ApiClient => {
  const fetchImpl = options.fetchImpl ?? defaultFetch
  const now = options.now ?? (() => new Date())
  const base = options.baseUrl.endsWith('/') ? options.baseUrl.slice(0, -1) : options.baseUrl

  const absoluteUrl = (path: string, query?: Record<string, QueryValue>) => `${base}${path}${searchParams(query)}`

  const report = (kind: ServiceSignalKind | null, detail: string) => {
    if (kind !== null) options.onSignal?.({ kind, detail, at: now().toISOString() })
  }

  const send = (req: ApiRequest): Promise<Response> =>
    fetchImpl(absoluteUrl(req.path, req.query), buildInit(options.getTenantId(), req)).catch((cause: unknown) => {
      report(signalFor(req.path, 'UPSTREAM_UNAVAILABLE', 0), API_COPY.offline)
      throw new ApiError('UPSTREAM_UNAVAILABLE', API_COPY.offline, { status: 0, path: req.path, cause })
    })

  const malformed = (response: Response, path: string): ApiError => {
    if (response.status === 401) options.onUnauthenticated?.()
    return new ApiError('INTERNAL', API_COPY.malformed, { status: response.status, path })
  }

  const refused = (response: Response, path: string, error: { code: ErrorCode; message: string } | null): ApiError => {
    const code = error?.code ?? 'INTERNAL'
    const message = error?.message ?? ERROR_MESSAGES.INTERNAL
    if (response.status === 401) options.onUnauthenticated?.()
    report(signalFor(path, code, response.status), message)
    return new ApiError(code, message, { status: response.status, path })
  }

  const request = async <T>(schema: z.ZodType<T>, req: ApiRequest): Promise<ApiResult<T>> => {
    const response = await send(req)
    const payload: unknown = await response.json().catch(() => null)
    const envelope = envelopeSchema(schema).safeParse(payload)
    if (!envelope.success) throw malformed(response, req.path)

    const { success, data, error, meta } = envelope.data
    if (!success || error !== null) throw refused(response, req.path, error)

    return meta === undefined ? { data: data as T } : { data: data as T, meta }
  }

  const download = async (req: ApiDownloadRequest): Promise<ApiDownload> => {
    const response = await send({ ...req, method: 'GET' })
    const disposition = response.headers.get('content-disposition')
    if (response.ok && disposition !== null && ATTACHMENT.test(disposition)) {
      return { blob: await response.blob(), filename: filenameOfDisposition(disposition) }
    }
    if (response.ok) throw malformed(response, req.path)
    const envelope = envelopeSchema(z.unknown()).safeParse(await response.json().catch(() => null))
    if (!envelope.success) throw malformed(response, req.path)
    throw refused(response, req.path, envelope.data.error)
  }

  return { request, download }
}
